import { useState, useEffect } from "react";
import {
  DataGrid,
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
} from "@mui/x-data-grid";
import { Button, Box } from "@mui/material";
import { useTheme } from "@emotion/react";
import AddIcon from "@mui/icons-material/Add";

// Components
import Group from "../modals/Group";

// Context
import { trackEvent } from "../utils/Utils";

// Theme
import { tokens } from "../../theme";

// Supabase
import supabase from "../../supabaseClient";

// Translations
import { useTranslation } from "react-i18next";

const columns = [
  { field: "id", width: 230 },
  { field: "name", width: 200 },
  { field: "description", width: 400 },
];

export default function GroupsTab() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { t } = useTranslation(); // Translation hook
  const [groups, setGroups] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);

  useEffect(() => {
    const fetchGroups = async () => {
      const { data, error } = await supabase.from("groups").select("*");

      if (!error) {
        setGroups(data);
      }
    };

    fetchGroups();
  }, [open]);

  const handleRowClick = async (params) => {
    trackEvent("groupstab-group_row_clicked");
    setSelectedGroup(params.row);
    setOpen(true);
  };

  const handleClose = () => {
    trackEvent("groupstab-group_modal_closed");
    setOpen(false);
    setSelectedGroup(null);
  };

  const handleNewGroup = () => {
    trackEvent("groupstab-new_group_clicked");
    setSelectedGroup({ name: "", description: "" });
    setOpen(true);
  };

  return (
    <Box>
      <Box
        sx={{
          height: "auto",
          minHeight: "400px",
          width: "100%",
          "& .MuiDataGrid-root": {
            border: "none",
            bgcolor: "transparent",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255, 255, 255, 0.05)" : "1px solid rgba(0, 0, 0, 0.05)",
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
            borderBottom: "none",
            color: theme.palette.mode === 'dark' ? "#a1a4ab" : "#141b2d",
            fontWeight: "bold",
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor: "transparent",
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "none",
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(0,0,0,0.2)" : "#f8f9fa",
            borderBottomLeftRadius: "16px",
            borderBottomRightRadius: "16px",
          },
          "& .MuiCheckbox-root": {
            color: `${theme.palette.mode === 'dark' ? "#4cceac" : "#3da58a"} !important`,
          },
          "& .MuiDataGrid-toolbarContainer .MuiButton-text": {
            color: `${theme.palette.mode === 'dark' ? "#a1a4ab" : "#727681"} !important`,
            fontWeight: "bold",
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.mode === 'dark' ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.02)",
          },
        }}
      >
        <DataGrid
          rows={groups}
          columns={columns.map((col) => ({
            ...col,
            headerName: t(`groupsTab.columns.${col.field}`), // Localized column headers
          }))}
          onRowClick={handleRowClick}
          disableSelectionOnClick
          autoHeight
          sx={{ borderRadius: "16px", overflow: "hidden" }}
          components={{
            Toolbar: () => (
              <GridToolbarContainer sx={{ p: 2, borderBottom: theme.palette.mode === 'dark' ? "1px solid rgba(255,255,255,0.05)" : "1px solid rgba(0,0,0,0.05)", gap: 1 }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleNewGroup}
                  sx={{
                    borderRadius: "8px",
                    fontWeight: "bold",
                    background: "linear-gradient(90deg, #7949E7 0%, #9A67ED 100%)",
                    color: "#fff",
                    boxShadow: "0 4px 14px 0 rgba(121, 73, 231, 0.39)",
                    "&:hover": { background: "linear-gradient(90deg, #6B53BB 0%, #7949E7 100%)" }
                  }}
                >
                  {t("groupsTab.toolbar.newGroup")} {/* Localized button */}
                </Button>

                <Box sx={{ flexGrow: 1 }} />

                <GridToolbarFilterButton />
                <GridToolbarColumnsButton />
                <GridToolbarDensitySelector />
              </GridToolbarContainer>
            ),
          }}
        />
      </Box>

      <Group open={open} onClose={handleClose} group={selectedGroup} />
    </Box>
  );
}
